/**
 * Custom hooks for Suggestion AI Resolution
 * Handles AI-powered resolution, applying corrections, and knowledge base learning
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiPost, apiGet } from '@/lib/api-client';
import type {
  AIResolutionResult,
  BatchResolutionResponse,
  ApplyCorrectionRequest,
  ApplyCorrectionResponse,
  MarkResolvedRequest,
  MarkResolvedResponse,
  SimilarCorrectionResponse,
} from '@/types/api/suggestion-resolution';

const SUGGESTION_RESOLUTION_QUERY_KEYS = {
  all: ['suggestion-resolution'] as const,
  similarCorrections: (suggestionId: string) =>
    [...SUGGESTION_RESOLUTION_QUERY_KEYS.all, 'similar', suggestionId] as const,
};

/**
 * Hook to resolve all pending suggestions on a document with AI
 */
export function useResolveDocumentSuggestionsWithAI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string): Promise<BatchResolutionResponse> => {
      return await apiPost<BatchResolutionResponse>(
        `/documents/${documentId}/suggestions/resolve-with-ai`
      );
    },
    onSuccess: (_, documentId) => {
      // Invalidate document suggestions queries
      queryClient.invalidateQueries({ queryKey: ['document-suggestions', documentId] });
    },
  });
}

/**
 * Hook to resolve a single suggestion with AI
 */
export function useResolveSingleSuggestionWithAI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (suggestionId: string): Promise<AIResolutionResult> => {
      return await apiPost<AIResolutionResult>(
        `/suggestions/${suggestionId}/resolve-with-ai`
      );
    },
    onSuccess: () => {
      // Invalidate suggestion queries
      queryClient.invalidateQueries({ queryKey: ['document-suggestions'] });
    },
  });
}

/**
 * Hook to apply an AI-suggested correction to a suggestion
 */
export function useApplyAICorrection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      suggestionId,
      correctedValue,
    }: {
      suggestionId: string;
      correctedValue: string;
    }): Promise<ApplyCorrectionResponse> => {
      const request: ApplyCorrectionRequest = {
        corrected_value: correctedValue,
      };
      return await apiPost<ApplyCorrectionResponse>(
        `/suggestions/${suggestionId}/apply-ai-correction`,
        request
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

/**
 * Hook to mark a suggestion as resolved and optionally add to knowledge base
 */
export function useMarkSuggestionResolved() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      suggestionId,
      correctedValue,
      resolutionNotes,
      addToKnowledgeBase = true,
    }: {
      suggestionId: string;
      correctedValue: string;
      resolutionNotes?: string;
      addToKnowledgeBase?: boolean;
    }): Promise<MarkResolvedResponse> => {
      const request: MarkResolvedRequest = {
        corrected_value: correctedValue,
        resolution_notes: resolutionNotes,
        add_to_knowledge_base: addToKnowledgeBase,
      };
      return await apiPost<MarkResolvedResponse>(
        `/suggestions/${suggestionId}/mark-resolved`,
        request
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-suggestions'] });
    },
  });
}

/**
 * Hook to find similar past corrections from the knowledge base
 */
export function useSimilarCorrections(suggestionId: string, enabled = true) {
  return useQuery({
    queryKey: SUGGESTION_RESOLUTION_QUERY_KEYS.similarCorrections(suggestionId),
    queryFn: async (): Promise<SimilarCorrectionResponse[]> => {
      return await apiGet<SimilarCorrectionResponse[]>(
        `/suggestions/${suggestionId}/similar-corrections`
      );
    },
    enabled: enabled && !!suggestionId,
  });
}
